import { AbstractControl, AsyncValidator, ValidationErrors } from '@angular/forms';
import { SignupService } from './signup.service';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { Observable, of, timer } from 'rxjs';
import { Injectable } from '@angular/core';
import { VatUtils } from '@app/shared/vat';
import {getVATCountryShort} from "@app/signup/shared/get-vat-country-short.helper";

// https://ec.europa.eu/taxation_customs/vies/faq.html#item_18


@Injectable({
  providedIn: 'root'
})
export class VatValidator implements AsyncValidator {
  selectedCountry;

  constructor(private signupService: SignupService) {
  }

  validate(
    ctrl: AbstractControl
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    if (ctrl.value === null || ctrl.value.length === 0 || !VatUtils.isEU(this.selectedCountry.short?.toUpperCase?.() ?? '')) {
      return of(null);
    }

    const vatNumber = ctrl.value.replace(/[\s-.,\xA0]|\xC2\xA0/, '');

    // Check the vat format by country
    if (typeof this.selectedCountry.short === 'string') {
      const countryRegExp = vatRegExps[this.selectedCountry.short.toLowerCase()];

      if (countryRegExp !== undefined) {
        const vatFormatOk = countryRegExp.test(vatNumber);

        if (!vatFormatOk) {
          return of({ 'vatFormat': { value: ctrl.value } });
        }
      }
    }

    // Check vat api
    return timer(1000).pipe(
      switchMap(() => {
        const countryShort = getVATCountryShort(this.selectedCountry.short);
        return this.signupService.checkVat(countryShort + ctrl.value).pipe(
          map(result => {
            if (result) {
              return result.data.is_valid ? null : { 'vatCheck': { value: ctrl.value } };
            }
          }),
          catchError(() => of(null))
        );
      })
    ).pipe(take(1), finalize(() => {
      // update step validity after async api check
      if (ctrl.root !== ctrl) {
        ctrl.root.updateValueAndValidity({ onlySelf: false });
      } else {
        ctrl.setErrors(ctrl.errors);
      }
    }));
  }

  generatePlaceholder(shortCountry: string): string {
    return this.getVatPlaceholderList(shortCountry);
  }

  getVatPlaceholderList(shortCountry: string): string {
    switch (shortCountry) {
      case 'AT':
        return 'UXXXXXXXX';
      case 'BE':
        return '0XXXXXXXXX';
      case 'BG':
        return 'XXXXXXXXX';
      case 'HR':
        return 'XXXXXXXXXXX';
      case 'CY':
        return 'XXXXXXXXL';
      case 'CZ':
        return 'XXXXXXXX';
      case 'DE':
        return 'XXXXXXXXX';
      case 'DK':
        return 'XXXXXXXX';
      case 'EE':
        return 'XXXXXXXXX';
      case 'EL':
        return 'XXXXXXXXX';
      case 'GR':
        return 'XXXXXXXXX';
      case 'ES':
        return 'EXXXXXXXV';
      case 'FI':
        return 'XXXXXXXX';
      case 'FR':
        return 'XXXXXXXXXXX';
      case 'HU':
        return 'XXXXXXXX';
      case 'IE':
        return '0SXXXXXXXXL';
      case 'IT':
        return 'XXXXXXXXXXXX';
      case 'LT':
        return 'XXXXXXXXX';
      case 'LU':
        return 'XXXXXXXX';
      case 'LV':
        return 'XXXXXXXX';
      case 'MT':
        return 'XXXXXXXX';
      case 'NL':
        return 'XXXXXXXXXBXX';
      case 'PL':
        return 'XXXXXXXXXX';
      case 'PT':
        return 'XXXXXXXXX';
      case 'RO':
        return 'XXXXXXXXXX';
      case 'SE':
        return 'XXXXXXXXXXXX';
      case 'SI':
        return 'XXXXXXXX';
      case 'SK':
        return 'XXXXXXXXXX';
      default:
        return '';
    }
  }
}

/**
 * Collection of reusable RegExps
 */
export const vatRegExps: { [key: string]: RegExp } = {
  // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s21.html
  // ^(
  //     (AT)?U[0-9]{8} |                              # Austria
  //     (BE)?0[0-9]{9} |                              # Belgium
  //     (BG)?[0-9]{9,10} |                            # Bulgaria
  //     (CY)?[0-9]{8}L |                              # Cyprus
  //     (CZ)?[0-9]{8,10} |                            # Czech Republic
  //     (DE)?[0-9]{9} |                               # Germany
  //     (DK)?[0-9]{8} |                               # Denmark
  //     (EE)?[0-9]{9} |                               # Estonia
  //     (EL|GR)?[0-9]{9} |                            # Greece
  //     (ES)?[0-9A-Z][0-9]{7}[0-9A-Z] |               # Spain
  //     (FI)?[0-9]{8} |                               # Finland
  //     (FR)?[0-9A-Z]{2}[0-9]{9} |                    # France
  //     (GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3}) | # United Kingdom
  //     (HU)?[0-9]{8} |                               # Hungary
  //     (IE)?[0-9]S[0-9]{5}L |                        # Ireland
  //     (IT)?[0-9]{11} |                              # Italy
  //     (LT)?([0-9]{9}|[0-9]{12}) |                   # Lithuania
  //     (LU)?[0-9]{8} |                               # Luxembourg
  //     (LV)?[0-9]{11} |                              # Latvia
  //     (MT)?[0-9]{8} |                               # Malta
  //     (NL)?[0-9]{9}B[0-9]{2} |                      # Netherlands
  //     (PL)?[0-9]{10} |                              # Poland
  //     (PT)?[0-9]{9} |                               # Portugal
  //     (RO)?[0-9]{2,10} |                            # Romania
  //     (SE)?[0-9]{12} |                              # Sweden
  //     (SI)?[0-9]{8} |                               # Slovenia
  //     (SK)?[0-9]{10}                                # Slovakia
  // )$
  // eslint-disable-next-line max-len
  eu: /^((AT)?U[0-9]{8}|(BE)?[01][0-9]{9}|(BG)?[0-9]{9,10}|(CY)?[0-9]{8}[A-Z]}|(CZ)?[0-9]{8,10}|(DE)?[0-9]{9}|(DK)?[0-9]{8}|(EE)?[0-9]{9}|(EL|GR)?[0-9]{9}|(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]|(FI)?[0-9]{8}|(FR)?[0-9A-Z]{2}[0-9]{9}|(HR)?[0-9]{11}|(HU)?[0-9]{8}|(IE)?[0-9A-Z*+]{7}[A-Z]{1,2}|(IT)?[0-9]{11}|(LT)?([0-9]{9}|[0-9]{12})|(LU)?[0-9]{8}|(LV)?[0-9]{11}|(MT)?[0-9]{8}|(NL)?[0-9]{9}B[0-9]{2}|(PL)?[0-9]{10}|(PT)?[0-9]{9}|(RO)?[0-9]{2,10}|(SE)?[0-9]{12}|(SI)?[0-9]{8}|(SK)?[0-9]{10})$/,
  at: /^(AT)?U[0-9]{8}$/,
  be: /^(BE)?[01][0-9]{9}$/,
  bg: /^(BG)?[0-9]{9,10}$/,
  cy: /^(CY)?[0-9]{8}[A-Z]$/,
  cz: /^(CZ)?[0-9]{8,10}$/,
  de: /^(DE)?[0-9]{9}$/,
  dk: /^(DK)?[0-9]{8}$/,
  ee: /^(EE)?[0-9]{9}$/,
  gr: /^(EL|GR)?[0-9]{9}$/,
  es: /^(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]$/,
  fi: /^(FI)?[0-9]{8}$/,
  fr: /^(FR)?[0-9A-Z]{2}[0-9]{9}$/,
  // Brexit gb: /^(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})$/,
  hr: /^(HR)?[0-9]{11}$/,
  hu: /^(HU)?[0-9]{8}$/,
  ie: /^(IE)?[0-9A-Z*+]{7}[A-Z]{1,2}$/,
  it: /^(IT)?[0-9]{11}$/,
  lt: /^(LT)?([0-9]{9}|[0-9]{12})$/,
  lu: /^(LU)?[0-9]{8}$/,
  lv: /^(LV)?[0-9]{11}$/,
  mt: /^(MT)?[0-9]{8}$/,
  nl: /^(NL)?[0-9]{9}B[0-9]{2}$/,
  pl: /^(PL)?[0-9]{10}$/,
  pt: /^(PT)?[0-9]{9}$/,
  ro: /^(RO)?[0-9]{2,10}$/,
  se: /^(SE)?[0-9]{12}$/,
  si: /^(SI)?[0-9]{8}$/,
  sk: /^(SK)?[0-9]{10}$/,
};
